import React, { useRef, useEffect, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, ChevronDown, MessageCircle, Headphones, Heart, BookOpen, Shield, Users, TrendingUp } from 'lucide-react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import AsesoriaTributariaPage from './AsesoriaTributaria';

gsap.registerPlugin(ScrollTrigger);

// ==================== SCENE DATA ====================
const SCENES = [
  {
    image: "https://static.prod-images.emergentagent.com/jobs/25e08005-62a4-450c-b29f-5128c7b420aa/images/fb8c1c127811ace7f8493254f6daac0e1870f8db65ff74562d95bc47828863d2.jpeg",
    caption: "",
    pan: { scale: 1.25, x: -30, y: -15 },
  },
  {
    image: "https://static.prod-images.emergentagent.com/jobs/25e08005-62a4-450c-b29f-5128c7b420aa/images/d8e3595f2795e3c76bc465819908d6c474e04acfbdd27a3ce9a0f7b18bbe421e.jpeg",
    caption: "Las cuentas se acumulan...",
    pan: { scale: 1.2, x: 25, y: -10 },
  },
  {
    image: "https://static.prod-images.emergentagent.com/jobs/25e08005-62a4-450c-b29f-5128c7b420aa/images/eb872f3c20e08555e310d0a2bc2835be16209f3bdb4873d167a4b1f4608811ce.jpeg",
    caption: "Pero hay una salida.",
    pan: { scale: 1.22, x: -20, y: 12 },
  },
  {
    image: "https://static.prod-images.emergentagent.com/jobs/25e08005-62a4-450c-b29f-5128c7b420aa/images/a4c7afc231f35b6d03c700c94a9c3d70432a5a9343277515dbb5790895c594fb.jpeg",
    caption: "Encuentras a tu asesor.",
    pan: { scale: 1.18, x: 30, y: -18 },
  },
  {
    image: "https://static.prod-images.emergentagent.com/jobs/25e08005-62a4-450c-b29f-5128c7b420aa/images/ead898f088139695d06c2910bfaafb62ef7a9f754f3d7ef36d30af7c5cd9acdd.jpeg",
    caption: "Construyen un plan juntos.",
    pan: { scale: 1.25, x: -25, y: 15 },
  },
  {
    image: "https://static.prod-images.emergentagent.com/jobs/25e08005-62a4-450c-b29f-5128c7b420aa/images/01d76395ba46a7adacab38b091d6647158df1ac718b60a60423eb28dcf9f6d18.jpeg",
    caption: "Empiezas a ahorrar de verdad.",
    pan: { scale: 1.2, x: 15, y: -12 },
  },
  {
    image: "https://static.prod-images.emergentagent.com/jobs/25e08005-62a4-450c-b29f-5128c7b420aa/images/a355e0d4b38804d38d29471509184e476c5940d015226202343ef62c835d032f.jpeg",
    caption: "Tu dinero crece contigo.",
    pan: { scale: 1.22, x: -30, y: 10 },
  },
  {
    image: "https://static.prod-images.emergentagent.com/jobs/25e08005-62a4-450c-b29f-5128c7b420aa/images/7d4d3eb47a40c4b32a81ea387aeaecaf07a609ea3d638425960f72979721a8e5.jpeg",
    caption: "Tu dinero trabaja para ti.",
    pan: { scale: 1.18, x: 20, y: -15 },
  },
];

// ==================== CINEMATIC SCROLL ====================
const CinematicScroll = () => {
  const sectionRef = useRef(null);

  useGSAP(() => {
    if (!sectionRef.current) return;

    const sceneEls = gsap.utils.toArray('.cinema-scene');
    const captionEls = gsap.utils.toArray('.cinema-caption');
    const progressFill = sectionRef.current.querySelector('.cinema-progress-fill');

    if (!sceneEls.length) return;

    // Responsive: reduce scroll distance & pan on mobile
    const isMobile = window.innerWidth < 768;
    const panScale = isMobile ? 0.45 : 1;
    const scrollDistance = isMobile ? 4500 : 8000;

    // Initial state: all scenes except first are invisible
    gsap.set(sceneEls.slice(1), { opacity: 0 });
    gsap.set(captionEls, { opacity: 0, y: isMobile ? 30 : 50 });

    const SLOT = 1;

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: sectionRef.current,
        start: 'top top',
        end: `+=${scrollDistance}`,
        scrub: isMobile ? 0.3 : 0.5,
        pin: true,
        anticipatePin: 1,
        onUpdate: (self) => {
          if (progressFill) {
            gsap.set(progressFill, { scaleY: self.progress });
          }
        },
      },
    });

    sceneEls.forEach((sceneEl, i) => {
      const img = sceneEl.querySelector('.cinema-img');
      if (!img) return;

      const start = i * SLOT;
      const isFirst = i === 0;
      const isLast = i === sceneEls.length - 1;
      const { scale: endScale, x: endX, y: endY } = SCENES[i].pan;

      // --- SCENE ENTRANCE (crossfade from previous) ---
      if (!isFirst) {
        tl.fromTo(
          sceneEl,
          { opacity: 0 },
          { opacity: 1, duration: 0.2, ease: 'power2.inOut' },
          start - 0.1
        );
      }

      // --- KEN BURNS: continuous zoom + pan (scaled for mobile) ---
      tl.fromTo(
        img,
        { scale: 1, x: 0, y: 0 },
        {
          scale: 1 + (endScale - 1) * panScale,
          x: endX * panScale,
          y: endY * panScale,
          duration: SLOT,
          ease: 'none',
        },
        start
      );

      // --- CAPTION IN ---
      if (SCENES[i].caption) {
        tl.fromTo(
          captionEls[i],
          { opacity: 0, y: isMobile ? 30 : 50 },
          { opacity: 1, y: 0, duration: 0.12, ease: 'power3.out' },
          start + 0.12
        );
      }

      // --- CAPTION OUT ---
      if (SCENES[i].caption && !isLast) {
        tl.to(
          captionEls[i],
          { opacity: 0, y: isMobile ? -15 : -30, duration: 0.1, ease: 'power2.in' },
          start + SLOT - 0.22
        );
      }

      // --- SCENE EXIT ---
      if (!isLast) {
        tl.to(
          sceneEl,
          { opacity: 0, duration: 0.2, ease: 'power2.inOut' },
          start + SLOT - 0.1
        );
      }
    });
  }, { scope: sectionRef });

  return (
    <section
      ref={sectionRef}
      className="relative h-screen w-full overflow-hidden bg-[#0a0e1a]"
      data-testid="cinematic-scroll"
    >
      {/* Scene layers */}
      {SCENES.map((scene, i) => (
        <div key={i} className="cinema-scene absolute inset-0 overflow-hidden">
          <img
            src={scene.image}
            alt=""
            className="cinema-img absolute inset-0 w-full h-full object-cover"
            style={{ transformOrigin: 'center center' }}
          />
        </div>
      ))}

      {/* Cinematic vignette overlay */}
      <div
        className="absolute inset-0 pointer-events-none z-[2]"
        style={{
          background: `
            linear-gradient(to bottom, rgba(10,14,26,0.55) 0%, transparent 22%, transparent 55%, rgba(10,14,26,0.9) 100%),
            radial-gradient(ellipse at center, transparent 40%, rgba(10,14,26,0.5) 100%)
          `,
        }}
      />

      {/* Caption layers */}
      <div className="absolute bottom-12 sm:bottom-16 md:bottom-24 inset-x-0 z-[3] pointer-events-none px-4 sm:px-6">
        {SCENES.map((scene, i) => (
          <p
            key={i}
            className="cinema-caption absolute inset-x-0 text-center text-base sm:text-xl md:text-3xl lg:text-4xl text-white/90 font-light tracking-wide max-w-3xl mx-auto"
            style={{
              fontFamily: 'Playfair Display, serif',
              textShadow: '0 4px 40px rgba(0,0,0,0.95), 0 1px 6px rgba(0,0,0,0.8)',
            }}
          >
            {scene.caption}
          </p>
        ))}
      </div>

      {/* Progress bar */}
      <div className="absolute right-3 sm:right-6 md:right-10 top-1/2 -translate-y-1/2 z-[4]">
        <div className="w-[2px] h-20 sm:h-28 bg-white/10 rounded-full relative overflow-hidden">
          <div
            className="cinema-progress-fill absolute bottom-0 left-0 w-full h-full bg-[#D4AF37] rounded-full origin-bottom"
            style={{ transform: 'scaleY(0)' }}
          />
        </div>
      </div>

      {/* Scroll mouse indicator */}
      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 z-[4] pointer-events-none opacity-60">
        <div className="w-5 h-8 rounded-full border border-white/30 flex items-start justify-center pt-1.5">
          <div className="w-[3px] h-[6px] bg-[#D4AF37] rounded-full animate-[scrollPulse_2s_ease-in-out_infinite]" />
        </div>
      </div>
    </section>
  );
};

// ==================== LOADING SCREEN ====================
const CinematicLoadingScreen = ({ progress }) => (
  <div className="h-screen flex items-center justify-center bg-[#0a0e1a]" data-testid="cinematic-loading">
    <div className="text-center">
      <div className="w-48 h-[2px] bg-[#1a2332] rounded-full overflow-hidden mb-4">
        <div
          className="h-full bg-[#D4AF37] rounded-full transition-all duration-500 ease-out"
          style={{ width: `${progress * 100}%` }}
        />
      </div>
      <p className="text-gray-600 text-[11px] tracking-[0.25em] uppercase">Cargando experiencia</p>
    </div>
  </div>
);

// ==================== WRAPPER (preloads images) ====================
const CinematicScrollWrapper = () => {
  const [ready, setReady] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    let loaded = 0;
    const total = SCENES.length;
    const promises = SCENES.map(
      (scene) =>
        new Promise((resolve) => {
          const img = new Image();
          img.onload = () => {
            loaded++;
            setProgress(loaded / total);
            resolve();
          };
          img.onerror = () => {
            loaded++;
            setProgress(loaded / total);
            resolve();
          };
          img.src = scene.image;
        })
    );
    Promise.all(promises).then(() => setReady(true));
  }, []);

  if (!ready) return <CinematicLoadingScreen progress={progress} />;
  return <CinematicScroll />;
};

// ==================== FADE IN SECTION ====================
const FadeSection = ({ children, className = '', delay = 0 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.3 });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay, ease: [0.25, 0.1, 0.25, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// ==================== ADVISOR QUALITY CARD ====================
const QualityCard = ({ icon: Icon, title, description, delay }) => (
  <FadeSection delay={delay}>
    <div className="group p-6 rounded-2xl bg-[#0f1520] border border-[#1a2332] hover:border-[#D4AF37]/30 transition-all duration-500">
      <div className="w-10 h-10 rounded-xl bg-[#D4AF37]/10 flex items-center justify-center mb-4 group-hover:bg-[#D4AF37]/20 transition-colors">
        <Icon className="w-5 h-5 text-[#D4AF37]" />
      </div>
      <h4 className="text-white font-semibold mb-2 text-base">{title}</h4>
      <p className="text-gray-400 text-sm leading-relaxed">{description}</p>
    </div>
  </FadeSection>
);

// ==================== WHY US ITEM ====================
const WhyUsItem = ({ number, title, description, delay }) => (
  <FadeSection delay={delay}>
    <div className="flex gap-6 group">
      <div className="shrink-0 w-12 h-12 rounded-full bg-[#D4AF37]/10 border border-[#D4AF37]/20 flex items-center justify-center text-[#D4AF37] font-bold text-lg group-hover:bg-[#D4AF37] group-hover:text-[#0a0e1a] transition-all duration-500">
        {number}
      </div>
      <div>
        <h4 className="text-white font-semibold mb-2 text-lg">{title}</h4>
        <p className="text-gray-400 text-sm leading-relaxed">{description}</p>
      </div>
    </div>
  </FadeSection>
);

// ==================== MAIN PAGE ====================
const AsesoriaFinancieraPage = () => {
  const heroRef = useRef(null);
  const { scrollYProgress: heroScroll } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  });
  const heroOpacity = useTransform(heroScroll, [0, 0.8], [1, 0]);
  const heroScale = useTransform(heroScroll, [0, 1], [1, 1.1]);

  return (
    <div className="bg-[#0a0e1a] text-white" data-testid="service-detail-page">
      {/* Custom animation keyframes */}
      <style>{`
        @keyframes scrollPulse {
          0%, 100% { opacity: 1; transform: translateY(0); }
          50% { opacity: 0.3; transform: translateY(4px); }
        }
      `}</style>

      {/* ==================== HERO ==================== */}
      <section
        ref={heroRef}
        className="relative h-screen flex items-center justify-center overflow-hidden"
      >
        <motion.div className="absolute inset-0 bg-[#0a0e1a]" style={{ scale: heroScale }}>
          <div
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage:
                'radial-gradient(circle at 30% 50%, #D4AF37 0%, transparent 50%), radial-gradient(circle at 70% 80%, #1a2332 0%, transparent 40%)',
            }}
          />
        </motion.div>

        <motion.div
          className="relative z-10 max-w-4xl mx-auto px-4 text-center"
          style={{ opacity: heroOpacity }}
        >
          <Link
            to="/servicios"
            className="inline-flex items-center text-gray-500 hover:text-white mb-8 transition-colors text-sm"
            data-testid="back-to-services"
          >
            <ArrowLeft className="mr-2" size={16} /> Servicios
          </Link>

          <motion.p
            className="text-[#D4AF37] text-sm tracking-[0.3em] uppercase mb-6 font-medium"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            Asesoría Financiera
          </motion.p>

          <motion.h1
            className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold leading-[1.1] mb-6 sm:mb-8 px-2"
            style={{ fontFamily: 'Playfair Display, serif' }}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.8 }}
          >
            No te vamos a decir{' '}
            <span className="italic text-[#D4AF37]">&ldquo;ahorra más&rdquo;</span>
          </motion.h1>

          <motion.p
            className="text-base sm:text-lg md:text-xl text-gray-400 max-w-2xl mx-auto leading-relaxed px-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
          >
            Te vamos a ayudar a entender por qué no lo has logrado — y a construir un plan que sí
            funcione para ti.
          </motion.p>
        </motion.div>

        <motion.div
          className="absolute bottom-8 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 2.5, ease: 'easeInOut' }}
        >
          <ChevronDown size={24} className="text-[#D4AF37]/60" />
        </motion.div>
      </section>

      {/* ==================== CINEMATIC SCROLL ANIMATION ==================== */}
      <CinematicScrollWrapper />

      {/* ==================== WHAT MAKES A GOOD ADVISOR ==================== */}
      <section className="py-16 sm:py-24 md:py-32 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0e1a] via-[#0c1018] to-[#0a0e1a]" />
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeSection>
            <p className="text-[#D4AF37] text-xs sm:text-sm tracking-[0.2em] uppercase mb-3 sm:mb-4 text-center font-medium">
              Lo que nos diferencia
            </p>
            <h2
              className="text-2xl sm:text-3xl md:text-4xl font-bold text-center mb-3 sm:mb-4 text-white"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              ¿Qué hace un asesor financiero{' '}
              <span className="text-[#D4AF37]">bueno para ti?</span>
            </h2>
            <p className="text-gray-500 text-center mb-10 sm:mb-16 max-w-xl mx-auto text-sm sm:text-base">
              Un buen asesor no te da fórmulas. Te da claridad.
            </p>
          </FadeSection>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            <QualityCard
              icon={Headphones}
              title="Te escucha antes de recomendarte algo"
              description="No llega con una fórmula lista; entiende tu contexto, tus miedos y tus metas primero."
              delay={0.1}
            />
            <QualityCard
              icon={BookOpen}
              title="Te educa, no solo te ejecuta"
              description="Te explica el 'por qué' detrás de cada decisión, para que tú también aprendas a decidir con criterio."
              delay={0.15}
            />
            <QualityCard
              icon={Heart}
              title="Se adapta a tu momento de vida"
              description="No es lo mismo asesorar a alguien saliendo de deudas que a alguien listo para invertir; ajusta el plan a donde estás."
              delay={0.2}
            />
            <QualityCard
              icon={Users}
              title="Te acompaña en el tiempo"
              description="El cambio de hábitos financieros no es inmediato; un buen asesor está presente en el proceso completo."
              delay={0.25}
            />
            <QualityCard
              icon={Shield}
              title="Es transparente"
              description="Te explica riesgos, costos y alternativas con claridad, sin presionarte a decisiones que no entiendes del todo."
              delay={0.3}
            />
            <QualityCard
              icon={MessageCircle}
              title="Prioriza tu bienestar"
              description="Su éxito se mide en tu estabilidad y tranquilidad financiera, no en cuántos productos te vendió."
              delay={0.35}
            />
          </div>
        </div>
      </section>

      {/* ==================== WHY CHOOSE US ==================== */}
      <section className="py-16 sm:py-24 md:py-32 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-[#D4AF37]/5 rounded-full blur-[100px] -translate-x-1/2 -translate-y-1/2" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <FadeSection>
            <p className="text-[#D4AF37] text-sm tracking-[0.2em] uppercase mb-4 font-medium">
              Nuestro enfoque
            </p>
            <h2
              className="text-3xl md:text-4xl font-bold mb-16 text-white"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              ¿Por qué escogernos <span className="text-[#D4AF37]">a nosotros?</span>
            </h2>
          </FadeSection>

          <div className="space-y-10">
            <WhyUsItem
              number={1}
              delay={0.1}
              title="No empezamos por el producto, empezamos por ti."
              description="Antes de hablarte de ahorro o inversión, entendemos tu personalidad financiera: cómo gastas, qué te genera ansiedad con el dinero, cuáles son tus hábitos reales (no los ideales). El plan se construye a partir de ti, no de una plantilla."
            />
            <WhyUsItem
              number={2}
              delay={0.15}
              title="Te acompañamos en el proceso completo."
              description="No es una consulta única ni una recomendación aislada. Vamos contigo desde el primer diagnóstico, pasando por la construcción de hábitos, hasta tu primera inversión — y seguimos presentes después."
            />
            <WhyUsItem
              number={3}
              delay={0.2}
              title="Educación real, no solo ejecución."
              description="No tomamos decisiones por ti sin explicarte el porqué. Cada recomendación viene acompañada de la lógica detrás, para que con el tiempo tú mismo tengas más criterio financiero, no más dependencia de nosotros."
            />
            <WhyUsItem
              number={4}
              delay={0.25}
              title="Somos un ecosistema, no un servicio aislado."
              description="Cuando tu situación financiera avanza — quieres invertir en finca raíz, formalizar un negocio, necesitas asesoría legal — no tienes que buscar otro proveedor. Contamos con una red de aliados especializados que ya conocen tu caso."
            />
            <WhyUsItem
              number={5}
              delay={0.3}
              title="Transparencia sobre presión de venta."
              description="No medimos éxito en cuántos productos te vendemos, sino en tu estabilidad financiera real. Si algo no te conviene en tu momento actual, te lo decimos — aunque eso signifique no cerrar una venta hoy."
            />
            <WhyUsItem
              number={6}
              delay={0.35}
              title="Entendemos al público joven porque hablamos su idioma."
              description="La mayoría de asesorías en Colombia están pensadas para perfiles con patrimonio consolidado. Nosotros trabajamos con personas que están empezando — con ingresos variables, primeras deudas, primeras metas — y ahí es donde más se necesita acompañamiento."
            />
          </div>
        </div>
      </section>

      {/* ==================== CTA ==================== */}
      <section className="py-20 sm:py-24 relative">
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(135deg, rgba(212,175,55,0.08) 0%, transparent 40%, rgba(212,175,55,0.05) 100%)',
          }}
        />
        <div className="relative max-w-3xl mx-auto px-4 text-center">
          <FadeSection>
            <h2
              className="text-2xl sm:text-3xl md:text-5xl font-bold mb-4 sm:mb-6"
              style={{ fontFamily: 'Playfair Display, serif' }}
            >
              ¿Listo para dar el <span className="text-[#D4AF37]">primer paso?</span>
            </h2>
            <p className="text-gray-400 text-base sm:text-lg mb-8 sm:mb-10 max-w-xl mx-auto">
              Elige cómo quieres empezar. Ya sea con un asesor a tu lado o explorando por tu cuenta.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://wa.me/573177848600?text=Hola%2C%20me%20interesa%20la%20asesor%C3%ADa%20financiera"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 bg-[#D4AF37] text-[#0a0e1a] px-8 sm:px-10 py-3.5 sm:py-4 rounded-full font-bold text-base sm:text-lg hover:bg-[#e8c84a] transition-all duration-300 hover:scale-105 hover:shadow-[0_0_40px_rgba(212,175,55,0.3)] w-full sm:w-auto justify-center"
                data-testid="contact-advisor-cta"
              >
                <MessageCircle size={20} />
                Contactar Asesor
              </a>
              <Link
                to="/finance"
                className="inline-flex items-center gap-3 border-2 border-[#D4AF37]/40 text-[#D4AF37] px-8 sm:px-10 py-3.5 sm:py-4 rounded-full font-bold text-base sm:text-lg hover:bg-[#D4AF37]/10 hover:border-[#D4AF37] transition-all duration-300 hover:scale-105 w-full sm:w-auto justify-center"
                data-testid="start-ld-finance-cta"
              >
                <TrendingUp size={20} />
                Comienza en LD Finance
              </Link>
            </div>
          </FadeSection>
        </div>
      </section>
    </div>
  );
};

// ==================== SERVICE DETAIL ROUTER ====================
const servicesBasicData = {
  'asesoria-legal': {
    title: 'Asesoría Legal',
    color: '#7B68EE',
    tagline: 'Protección jurídica que te da tranquilidad.',
    subtitle: 'Soporte legal integral para que tu empresa crezca sin riesgos.',
  },
  'fondo-de-inversion': {
    title: 'Fondo de Inversión',
    color: '#2ECC71',
    tagline: 'Haz que tu dinero trabaje mientras duermes.',
    subtitle: 'Fondos diversificados gestionados por expertos.',
  },
  'gestion-contable': {
    title: 'Gestión Contable',
    color: '#E67E22',
    tagline: 'Tus números en orden, tu negocio en control.',
    subtitle: 'Servicios contables integrales para decisiones basadas en datos.',
  },
  auditoria: {
    title: 'Auditoría',
    color: '#E74C3C',
    tagline: 'Transparencia que genera confianza.',
    subtitle: 'Auditorías rigurosas para la integridad de tu información financiera.',
  },
  'planeacion-financiera': {
    title: 'Planeación Financiera',
    color: '#1ABC9C',
    tagline: 'Un mapa claro hacia tus metas.',
    subtitle: 'Planes estratégicos que convierten objetivos en realidad.',
  },
  'consultoria-empresarial': {
    title: 'Consultoría Empresarial',
    color: '#9B59B6',
    tagline: 'Impulsa tu empresa al siguiente nivel.',
    subtitle: 'Consultoría estratégica para optimizar operaciones.',
  },
  'outsourcing-financiero': {
    title: 'Outsourcing Financiero',
    color: '#3498DB',
    tagline: 'Tu equipo financiero, sin el costo.',
    subtitle: 'Tercerización profesional de procesos financieros.',
  },
  'gestion-de-riesgos': {
    title: 'Gestión de Riesgos',
    color: '#F39C12',
    tagline: 'Anticípate a lo que podría salir mal.',
    subtitle: 'Identificación y mitigación de riesgos empresariales.',
  },
  'capacitacion-financiera': {
    title: 'Capacitación Financiera',
    color: '#16A085',
    tagline: 'El conocimiento financiero es poder.',
    subtitle: 'Formación diseñada para transformar tu relación con el dinero.',
  },
};

const GenericServicePage = ({ service }) => (
  <div className="bg-[#0a0e1a] text-white min-h-screen" data-testid="service-detail-page">
    <section className="h-[70vh] flex items-center justify-center relative">
      <div
        className="absolute inset-0 opacity-15"
        style={{
          backgroundImage: `radial-gradient(circle at 50% 50%, ${service.color} 0%, transparent 60%)`,
        }}
      />
      <div className="relative text-center max-w-3xl mx-auto px-4">
        <Link to="/servicios" className="inline-flex items-center text-gray-500 hover:text-white mb-6 text-sm">
          <ArrowLeft className="mr-2" size={16} /> Servicios
        </Link>
        <p className="text-sm tracking-[0.3em] uppercase mb-4 font-medium" style={{ color: service.color }}>
          {service.title}
        </p>
        <h1
          className="text-4xl md:text-6xl font-bold mb-6"
          style={{ fontFamily: 'Playfair Display, serif', color: service.color }}
        >
          {service.tagline}
        </h1>
        <p className="text-xl text-gray-400">{service.subtitle}</p>
      </div>
    </section>
    <section className="py-20 text-center">
      <p className="text-gray-500">Contenido completo próximamente.</p>
      <Link
        to="/contacto"
        className="inline-flex items-center gap-2 mt-8 px-8 py-3 rounded-full font-bold transition-all hover:scale-105"
        style={{ backgroundColor: service.color, color: '#0a0e1a' }}
        data-testid="generic-service-cta"
      >
        Contáctanos
      </Link>
    </section>
  </div>
);

const ServiceDetail = () => {
  const { slug } = useParams();

  if (slug === 'asesoria-financiera') {
    return <AsesoriaFinancieraPage />;
  }

  if (slug === 'asesoria-tributaria') {
    return <AsesoriaTributariaPage />;
  }

  const service = servicesBasicData[slug];
  if (!service) return <Navigate to="/servicios" replace />;

  return <GenericServicePage service={service} />;
};

export default ServiceDetail;
